/**
 * Content configuration for OrientationMode
 * Contains all messages, buttons, and phase definitions for the onboarding flow
 */

/**
 * Phase definitions for the orientation flow (v2 - 5 phases)
 *
 * Flow: INTRODUCTION → DISCOVERY → INSIGHT → ACCOUNT_PROMPT → TRANSITION
 */
export const ORIENTATION_PHASES = {
  INTRODUCTION: 'INTRODUCTION',
  DISCOVERY: 'DISCOVERY',
  INSIGHT: 'INSIGHT',
  ACCOUNT_PROMPT: 'ACCOUNT_PROMPT',
  TRANSITION: 'TRANSITION'
};

/**
 * Sub-states for diversions within phases
 */
export const ORIENTATION_SUB_STATES = {
  DISCUSSING_INSIGHT: 'discussing_insight',
  HANDLING_ADVICE: 'handling_advice'
};

/**
 * Phase to backend step mapping (v2)
 * Used for persistence via /api/guest/onboarding/step
 */
export const PHASE_TO_STEP = {
  [ORIENTATION_PHASES.INTRODUCTION]: 'INTRODUCTION',
  [ORIENTATION_PHASES.DISCOVERY]: 'DISCOVERY',
  [ORIENTATION_PHASES.INSIGHT]: 'INSIGHT',
  [ORIENTATION_PHASES.ACCOUNT_PROMPT]: 'ACCOUNT_PROMPT',
  [ORIENTATION_PHASES.TRANSITION]: 'TRANSITION'
};

/**
 * Backend step to phase mapping (for resumption)
 */
export const STEP_TO_PHASE = Object.fromEntries(
  Object.entries(PHASE_TO_STEP).map(([phase, step]) => [step, phase])
);

/**
 * Questionnaire IDs mapped to actual registry entries
 */
export const QUESTIONNAIRE_IDS = {
  LOVE_LANGUAGES: 'affectionpatterns'
};

/**
 * Dynamic header text for each phase/sub-phase
 * Some phases have multiple header states (before/after questionnaire)
 */
export const PHASE_HEADERS = {
  // Introduction
  INTRODUCTION_START: 'Getting Started',
  INTRODUCTION_NAMED: 'Welcome {name}',

  // Discovery (Love Languages questionnaire)
  DISCOVERY: 'How Questionnaires Work',

  // Insight (Love Languages result)
  INSIGHT: 'How Insights Work',

  // Account Prompt
  ACCOUNT_PROMPT: 'Save your progress',

  // Transition
  TRANSITION: 'See it in action',

  // Transition — questionnaire-skipped fork. There is no personalized result to
  // demonstrate, so the phase ends on next steps instead of a demo. Must stay a
  // unique string: _getCurrentHeaderKey reverse-looks-up PHASE_HEADERS by value,
  // so a duplicate value would resolve to whichever key is declared first.
  TRANSITION_SKIPPED: "What's next",

  // Completion beat — held for the length of the exit animation on every route
  // out of orientation (advice handoff, skip-and-exit, natural end of
  // TRANSITION), while the progress strip fills to 100%.
  COMPLETE: 'Orientation complete'
};

/**
 * Content for each phase (v2 - 5 phases)
 * Each phase can have multiple steps with messages, buttons, and input configurations
 */
export const ORIENTATION_CONTENT = {
  [ORIENTATION_PHASES.INTRODUCTION]: {
    steps: [
      {
        id: 'welcome',
        message: "Welcome!\n\nThink of me as your relationship guide. Together, we'll explore how you love, communicate, and connect.\n\nBut first — what's your name?",
        inputType: 'text',
        inputPlaceholder: 'Type your first name...',
        header: PHASE_HEADERS.INTRODUCTION_START,
        staggerDelay: 500
      },
      {
        id: 'acknowledge',
        messageVariants: {
          sso: "Nice to meet you, {name}.\n\nThink of me as your relationship guide. Together, we'll explore how you love, communicate, and connect.\n\nLet me show you how this works. This brief orientation will take about 5 minutes. Or you can exit and explore on your own, if you prefer.",
          default: "Nice to meet you, {name}.\n\nLet me show you how this works. This brief orientation will take about 5 minutes. Or you can exit and explore on your own, if you prefer.",
          nameless: "Sure thing.\n\nLet me show you how this works. This brief orientation will take about 5 minutes. Or you can exit and explore on your own, if you prefer."
        },
        buttons: [
          { label: 'Continue', action: 'continue', metadata: { recommended: true } },
          { label: 'Exit', action: 'dismiss', metadata: { subtle: true } }
        ],
        header: PHASE_HEADERS.INTRODUCTION_NAMED,
        staggerDelay: 500
      }
    ]
  },

  [ORIENTATION_PHASES.DISCOVERY]: {
    steps: [
      {
        id: 'questionnaire_intro',
        message: "One way I get to know you is through brief questionnaires. Each one explores a different part of how you think, feel, and relate to others.\n\nAs you answer, I build a picture of who you are — your patterns, your needs, your unique strengths. This becomes your profile, and it's what allows me to give you advice that actually fits *you*.\n\nLet's try one now. This first questionnaire explores how you experience love:",
        showQuestionnaireArtifact: true,
        questionnaireArtifactId: QUESTIONNAIRE_IDS.LOVE_LANGUAGES,
        header: PHASE_HEADERS.DISCOVERY,
        staggerDelay: 500,
        componentDelay: 2000,
        // Begin + Skip render into the chat input (see OrientationMode
        // _renderPostArtifactButtons + ORIENTATION_QUESTIONNAIRE_BUTTON_OPTIONS).
        // Begin opens the questionnaire panel; Skip wires the still-intact
        // skip_love_languages branch in OrientationMode.
        postArtifactButtons: [
          { label: 'Begin questionnaire', action: 'begin_questionnaire', metadata: { recommended: true } },
          { label: 'Skip questionnaire', action: 'skip_love_languages' }
        ]
      }
    ]
  },

  [ORIENTATION_PHASES.INSIGHT]: {
    steps: [
      {
        id: 'processing',
        message: "Let me think about this for a moment...",
        staggerDelay: 500,
        autoAdvanceDelay: 3000,
        header: PHASE_HEADERS.INSIGHT
      },
      {
        id: 'insight_display',
        messageVariants: {
          default: "Your answers reveal something interesting about how you experience love:",
          return: "Welcome back, {name}.\n\nLast time you completed the **Affection Patterns** questionnaire and your answers revealed something interesting about how you experience love:",
          return_nameless: "Welcome back.\n\nLast time you completed the **Affection Patterns** questionnaire and your answers revealed something interesting about how you experience love:",
          skipped: "No problem. You can always complete that questionnaire later.\n\nHere's an example of some observations we might have generated for that questionnaire. These are not personalized for you, only provided for reference."
        },
        // Rendered inline below the insight card, in the same message bubble as the
        // card (see OrientationMode._renderInlineInsight).
        insightTrailingMessage: "Do these observations feel right?",
        showInsight: true,
        insightType: 'affection_patterns',
        postArtifactButtons: [
          { label: 'Yes, that resonates', action: 'confirm_insight' },
          { label: 'Not quite', action: 'deny_insight' }
        ],
        // Skip path: the card is the generic example built by the skip_love_languages
        // handler (isGeneric, "(Example)" label), not a reading of this user. Asking
        // them to confirm it is incoherent, and answering it would corrupt downstream
        // state — `confirm_insight` sets insightConfirmed=true, which trips the
        // ACCOUNT_PROMPT `showUnderstanding` gate into showing a memory-update card
        // for a questionnaire they never took, while `deny_insight` makes the 'denied'
        // variant outrank 'skipped' in the variant chain. A plain Continue leaves
        // insightConfirmed null, so both stay correct.
        skippedInsightTrailingMessage: null,
        skippedPostArtifactButtons: [
          { label: 'Continue', action: 'continue', metadata: { recommended: true } }
        ],
        header: PHASE_HEADERS.INSIGHT,
        staggerDelay: 500,
        componentDelay: 1500
      }
    ]
  },

  [ORIENTATION_PHASES.ACCOUNT_PROMPT]: {
    steps: [
      {
        id: 'account_prompt',
        messageVariants: {
          // Try-then-keep, not gate-then-try: a guest can decline here and still
          // see the rest of orientation, including the advice demo. "We'll need
          // to create an account" was true of the old flow and is now both wrong
          // and pushy — the account is what KEEPS the profile, not what starts it.
          default: "Great. I'll save this new insight to my memory:\n\nThis insight is the beginning of your profile — a picture of who you are that I'll remember and build on over time. The more we talk and interact, the deeper my understanding grows.\n\nAn account is what keeps that profile — it takes a minute. You're also welcome to keep going as you are and decide later.",
          denied: "No problem at all. These early observations are just a starting point — the more we talk and the more questionnaires you complete, the deeper my understanding grows.\n\nAn account is what keeps that profile — it takes a minute. You're also welcome to keep going as you are and decide later.",
          skipped: "These types of insights form the beginning of your profile — a picture of who you are that I'll remember and build on over time.\n\nAn account is what keeps that profile — it takes a minute. You're also welcome to keep going as you are and decide later.",
          authenticated: "Great. I'll save this new insight to my memory: \n\nThis insight is the beginning of your profile — a picture of who you are that I'll remember and build on over time. The more we talk and interact, the deeper my understanding grows.\n\nSince you've already created an account, your profile is being saved automatically.",
          authenticated_denied: "No problem at all. These early observations are just a starting point — the more we talk and the more questionnaires you complete, the deeper my understanding grows.\n\nSince you've already created an account, your profile is being saved automatically. As you complete more questionnaires, I'll refine my understanding of who you are — and the advice I give will get more personalized.",
          authenticated_skipped: "These types of insights form the beginning of your profile — a picture of who you are that I'll remember and build on over time.\n\nSince you've already created an account, your profile is being saved automatically. As you complete more questionnaires, I'll refine my understanding of who you are — and the advice I give will get more personalized."
        },
        buttons: [
          { label: 'Create account', action: 'create_account', metadata: { recommended: true } },
          { label: 'Skip for now', action: 'defer_account' }
        ],
        authenticatedButtons: [
          { label: 'Continue', action: 'continue', metadata: { recommended: true } }
        ],
        showUnderstanding: true,
        header: PHASE_HEADERS.ACCOUNT_PROMPT,
        staggerDelay: 400
      }
    ]
  },

  // Account creation success (branch from account_prompt)
  ACCOUNT_SETUP: {
    steps: [
      {
        id: 'account_created',
        messageVariants: {
          default: "Thanks, {name} — your account is all set.\n\nFrom here on, what you share stays with me. Every questionnaire and every conversation adds to your profile, and I'll keep building on it as we go.",
          nameless: "Thanks — your account is all set.\n\nFrom here on, what you share stays with me. Every questionnaire and every conversation adds to your profile, and I'll keep building on it as we go."
        },
        buttons: [
          { label: 'Continue', action: 'continue', metadata: { recommended: true } }
        ],
        header: PHASE_HEADERS.ACCOUNT_PROMPT,
        staggerDelay: 400
      }
    ]
  },

  // Guest continues (branch from account_prompt)
  GUEST_CONTINUES: {
    steps: [
      {
        id: 'defer_acknowledged',
        // The old line here ("only saved in this browser… it'll be lost") was
        // simply untrue: guest work lives on a real row for about 7 days, found
        // again by this browser's session. Say what actually happens, and put
        // the account where it belongs — as what makes it permanent and
        // portable, not as a warning.
        message: "No problem — you can create an account anytime.\n\nI'll hold what we build for about 7 days, and it's tied to this browser. Creating an account is what makes it permanent and lets you pick it up anywhere.",
        buttons: [
          { label: 'Continue without account', action: 'continue' },
          { label: 'Create account', action: 'create_account' }
        ],
        header: PHASE_HEADERS.ACCOUNT_PROMPT,
        staggerDelay: 500
      }
    ]
  },

  [ORIENTATION_PHASES.TRANSITION]: {
    steps: [
      {
        id: 'wrap_up',
        messageVariants: {
          default: "OK, here's how it all comes together. The insights we discover together — like how you experience love — shape our future conversations. So the advice I can give you is personalized, not just generic tips.\n\nI'd love to show you, if you have a few minutes.",
          // Skip path: the demo the default copy offers cannot run. The
          // orientation advice session is built entirely on the affection-pattern
          // scores (see orientationAdvicePrompt.js — the arc names the give/receive
          // mismatch turn by turn), so a user who skipped the questionnaire has
          // nothing for it to work from. The phase ends on next steps instead of
          // offering a conversation there is no button for.
          skipped: "That completes our brief orientation. You've seen the shape of it: questionnaires and conversations turn into insights, and those insights are what let me give you advice that fits your situation rather than generic tips.\n\nNext, I'd recommend you begin **Starting Your Journey**. This path will help us build a baseline understanding of you and the issues you're wanting to work on.",
          // Same as `skipped`, plus the disclosure a deferred guest is owed:
          // "Start Your Journey" is the one button here that will ask for an
          // account, and finding that out only after clicking reads as a trick.
          skipped_deferred: "That completes our brief orientation. You've seen the shape of it: questionnaires and conversations turn into insights, and those insights are what let me give you advice that fits your situation rather than generic tips.\n\nNext, I'd recommend you begin **Starting Your Journey**. This path will help us build a baseline understanding of you and the issues you're wanting to work on — and since it's where your profile really starts, that's the point where I'll ask you to create your free account."
        },
        buttonGroups: [
          [
            { label: 'Discuss how you experience love', action: 'scenario_love_language', metadata: { recommended: true } },
            { label: 'Skip and exit', action: 'explore_own' }
          ]
        ],
        // Mirrors the post-advice-session terminus (`orientation-session-conclusion`
        // in backend/src/config/button-groups.js): Foundation primary, exit
        // secondary, same "Start Your Journey" label so the destination reads the
        // same here, there, and on the dashboard card that surfaces once
        // orientation completes.
        skippedButtonGroups: [
          [
            { label: 'Start Your Journey', action: 'complete_foundation', metadata: { recommended: true } },
            { label: 'Exit to dashboard', action: 'explore_own' }
          ]
        ],
        header: PHASE_HEADERS.TRANSITION,
        skippedHeader: PHASE_HEADERS.TRANSITION_SKIPPED,
        staggerDelay: 500
      }
    ]
  }
};

/**
 * Buttons shown after LLM handles user deviations
 */
export const DEVIATION_BUTTONS = {
  // Default buttons when user deviates during normal flow
  default: [
    { label: 'Continue', action: 'continue_setup' }
  ]
};

/**
 * Messages for handling edge cases and diversions
 */
export const DIVERSION_CONTENT = {
  // When user tries to chat during onboarding
  CHAT_DURING_ONBOARDING: {
    message: "I'm happy to help with that \u2014 and I will. But let's finish getting acquainted first so I have a better sense of who you are. This will only take a few more minutes.\n\nIf it's urgent, just let me know and we can pause.",
    buttons: [
      { label: 'Continue setup', action: 'continue_setup', metadata: { recommended: true } },
      { label: 'This is urgent', action: 'handle_urgent' }
    ],
    staggerDelay: 400
  },

  // Response for urgent requests (guest)
  URGENT_GUEST: {
    message: "[Provide general advice]\n\n*Since you're not signed in yet, I can't tailor this to your specific patterns. Once you create an account and complete more discovery, my advice will get much more specific to you.*",
    returnPrompt: "Ready to continue where we left off?",
    buttons: [
      { label: 'Continue setup', action: 'return_to_orientation', metadata: { recommended: true } }
    ]
  },

  // Insight discussion limit reached
  INSIGHT_DISCUSSION_LIMIT: {
    message: "I'd love to explore this more with you. Let's finish getting set up first, and then we can dive deeper into any of your insights whenever you want.",
    buttons: [
      { label: 'Continue', action: 'continue', metadata: { recommended: true } }
    ]
  },

  // Mode selector clicked during onboarding
  MODE_SELECTOR_TOOLTIP: "I'll switch modes automatically while we're getting set up. You can use this anytime after.",

  // Private mode clicked during onboarding
  PRIVATE_MODE_TOOLTIP: "Private mode lets you have conversations that aren't saved or used to build your profile. Want to learn more, or continue setting up?",

  // Sidebar tab clicked during onboarding
  SIDEBAR_BANNER: "You're in the middle of getting set up.",
  SIDEBAR_RETURN_LINK: "Continue where you left off \u2192"
};

/**
 * Interpolate content tokens in a template string
 * @param {string} template - Template with {token} placeholders
 * @param {Object} data - Data object with token values
 * @returns {string} Interpolated string
 */
export function interpolateContent(template, data) {
  if (!template) return '';

  return template.replace(/\{(\w+)\}/g, (match, key) => {
    const value = data[key];
    if (value === undefined || value === null) {
      console.warn(`[OrientationContent] Missing content token: ${key}`);
      // For name, warn that nameless variant should be used instead
      if (key === 'name') {
        console.warn('[OrientationContent] Name interpolation attempted with no name - use nameless variant instead');
        return ''; // Return empty string; nameless variants should be used
      }
      return '';
    }
    return value;
  });
}

/**
 * Get the next phase in the orientation flow
 * @param {string} currentPhase - Current phase name
 * @returns {string|null} Next phase name or null if complete
 */
export function getNextPhase(currentPhase) {
  const phases = Object.values(ORIENTATION_PHASES);
  const currentIndex = phases.indexOf(currentPhase);

  if (currentIndex === -1 || currentIndex >= phases.length - 1) {
    return null; // No next phase (complete)
  }

  return phases[currentIndex + 1];
}

/**
 * Maps 5 orientation phases to 5 progress bar steps (1:1 mapping)
 *
 * Step mapping:
 * 1: Introduction (welcome, name capture)
 * 2: Discovery (Love Languages questionnaire)
 * 3: Insight (insight display)
 * 4: Account (account prompt)
 * 5: Transition (wrap-up and next steps)
 */
export const ORIENTATION_PROGRESS_STEPS = {
  [ORIENTATION_PHASES.INTRODUCTION]: 1,
  [ORIENTATION_PHASES.DISCOVERY]: 2,
  [ORIENTATION_PHASES.INSIGHT]: 3,
  [ORIENTATION_PHASES.ACCOUNT_PROMPT]: 4,
  [ORIENTATION_PHASES.TRANSITION]: 5
};

/** Total number of progress bar steps */
export const ORIENTATION_TOTAL_STEPS = 5;
